import classNames from 'classnames';
import * as React from 'react';
import { useTheme } from '~/Features/Layouts/Theme/Context/ThemeContextProvider';
import { PortfolioItemInfo } from '~/Features/StudyPage/Campus/Sections/StudyPortfolioSection/PortfolioItem/PortfolioItemInfo/PortfolioItemInfo';
import type { AspectRatio } from '~/Partials/Picture/AspectRatios';
import { Picture } from '~/Partials/Picture/Picture';
import { PictureProfiles } from '~/Partials/Picture/PictureProfiles';
import { SvgIcon } from '~/Partials/SvgIcon/SvgIcon';
import { assertNever } from '~/Partials/Utils/typeUtils';
import { useClickProxyAllowingTextSelection } from '~/Utils/Hooks/useClickProxyAllowingTextSelection';
import styles from './PortfolioItem.module.css';
import itemStyles from './PortfolioItemInfo/PortfolioItemInfo.module.css';
import { getActionIcon, getTypeIcon } from './Utils/portfolioItemsUtils';
import type {
    AudioPortfolioItem,
    ImagePortfolioItem,
    PortfolioBackground,
    PortfolioTheme,
    VideoPortfolioItem,
} from './types';

export type PortfolioItemDetails = ImagePortfolioItem | VideoPortfolioItem | AudioPortfolioItem;

export interface PortfolioItemProps {
    item: PortfolioItemDetails;
    background: PortfolioBackground;
    ratio: AspectRatio;
    theme?: PortfolioTheme;
}

const getOrientation = (ratio: AspectRatio) => {
    switch (ratio) {
        case '1x1':
        case '2x3':
        case '3x4':
        case '9x16':
            return 'vertical';
        case '3x2':
        case '4x3':
        case '16x9':
            return 'horizontal';
        default:
            assertNever(ratio);
    }
};

export const PortfolioItem: React.FC<PortfolioItemProps> = props => {
    const contextualTheme = useTheme();
    const { item, background, ratio, theme = contextualTheme } = props;
    const { type, title, caption, image, link } = item;

    const { withRoundedCorners } = theme;

    const { clickableRef, onProxyMouseDown, onProxyMouseUp } = useClickProxyAllowingTextSelection<HTMLAnchorElement>();

    const typeIcon = getTypeIcon(type);
    const infoVisibleOnInteractOnly = type === 'image';
    const hasInfo = !!title || !!caption;

    return (
        <div
            {...(link && { role: 'link', onMouseDown: onProxyMouseDown, onMouseUp: onProxyMouseUp, tabIndex: -1 })}
            className={classNames(
                styles.PortfolioItem,
                styles[`Variant--${ratio}`],
                !image && styles[`Variant--${background}`],
                link && styles['Variant--withLink'],
                (link || (infoVisibleOnInteractOnly && hasInfo)) && styles['Variant--withOverlayAnimation'],
                withRoundedCorners && styles['Variant--rounded'],
                infoVisibleOnInteractOnly && itemStyles.VisibleOnInteractOnlyContainer
            )}
        >
            {image && <Picture {...image} profile={PictureProfiles.studyPortfolioItem} theme={theme} />}
            <div
                className={classNames(
                    styles.Overlay,
                    infoVisibleOnInteractOnly && styles['Overlay--initialTransparent']
                )}
            />
            <div className={styles.Content}>
                {typeIcon && <SvgIcon iconName={typeIcon} className={styles.TypeIcon} />}
                <div className={styles.Info}>
                    <PortfolioItemInfo
                        title={title}
                        linkRef={clickableRef}
                        link={link}
                        caption={caption}
                        visibleOnInteractOnly={infoVisibleOnInteractOnly}
                        iconName={getActionIcon(type)}
                        orientation={getOrientation(ratio)}
                    />
                </div>
            </div>
        </div>
    );
};
